<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import { getCurrentLocale } from '@/i18n';
import { ONEWAY, OPENJAW } from '@/constants';
import { formatDate } from '@/utils/dateUtils';
import { getTextVariant } from '@/utils/stringUtils';

interface Props {
  infoData: any;
}
defineProps<Props>();

const { t } = useI18n({ useScope: 'global' });
const currentLanguage = getCurrentLocale();

const getClassAir = (price: any) => {
  return t(`worksheet.${(price?.find((priceItem: any) => priceItem.class === 'Economy')?.type ?? 'Economy').toLowerCase()}`);
};

const getClassAirBusiness = (price: any) => {
  return t(`worksheet.${(price?.find((priceItem: any) => priceItem.class === 'Business')?.type ?? 'Business').toLowerCase()}`);
};

const getFlightEconomy = (price: any) => {
  return price?.find((priceItem: any) => priceItem.class === 'Economy');
};

const getFlightBusiness = (price: any) => {
  return price?.find((priceItem: any) => priceItem.class === 'Business');
};
</script>
<template>
  <div class="flight-info-wrapper">
    <div class="lead-option mb-2">{{ `${$t('common.flight')}` }}</div>
    <div class="detail-option flex justify-content-between flex-wrap lg:flex-nowrap pr-4">
      <div>
        <div class="itinerary font-bold mb-2 mt-2">
          <div class="flex gap-3">
            <div class="min-w-label">{{ $t('advance_quote.form.itinerary') }}:</div>
            <div class="font-normal">{{ $t(`advance_quote.form.${infoData.itinerary.toLowerCase()}`) }}</div>
          </div>
        </div>
        <div class="gateway font-bold mb-2 mt-2">
          <div class="flex gap-3">
            <div class="min-w-label">{{ $t('common.gateway') }}:</div>
            <div class="font-normal">{{ getTextVariant(currentLanguage, infoData?.gateway, infoData?.gatewayFr) }}</div>
          </div>
        </div>
        <div class="destination font-bold mb-2">
          <div class="flex gap-3">
            <div class="min-w-label">{{ $t('common.destination') }}:</div>
            <div class="font-normal">{{ getTextVariant(currentLanguage, infoData?.destination, infoData?.destinationFr) }}</div>
          </div>
        </div>
        <div class="returnFrom font-bold mb-2" v-if="infoData.itinerary === OPENJAW">
          <div class="flex gap-3">
            <div class="min-w-label">{{ $t('advance_quote.form.returning_from') }}:</div>
            <div class="font-normal">{{ getTextVariant(currentLanguage, infoData.returnFrom, infoData.returnFromFr) }}</div>
          </div>
        </div>
        <div class="returnTo font-bold mb-2" v-if="infoData.itinerary === OPENJAW">
          <div class="flex gap-3">
            <div class="min-w-label">{{ $t('advance_quote.form.returning_to') }}:</div>
            <div class="font-normal">{{ getTextVariant(currentLanguage, infoData.returnTo, infoData.returnToFr) }}</div>
          </div>
        </div>
        <div class="hotel font-bold mb-2">
          <div class="flex gap-3">
            <div class="min-w-label">{{ $t('worksheet.dep_date') }}:</div>
            <div class="font-normal">{{ formatDate(infoData?.depDate, 'dd-LLL-yyyy', false, currentLanguage) }}</div>
          </div>
        </div>
        <div class="hotel font-bold mb-2" v-if="infoData.itinerary !== ONEWAY">
          <div class="flex gap-3">
            <div class="min-w-label">{{ $t('worksheet.return_date') }}:</div>
            <div class="font-normal">{{ formatDate(infoData?.retDate, 'dd-LLL-yyyy', false, currentLanguage) }}</div>
          </div>
        </div>
      </div>
      <div class="flex flex-column align-items-end">
        <div class="hotel-room-cat font-bold mb-2 mt-2">
          <div class="flex">
            <div class="min-w-label">{{ $t('common.class') }}:</div>
            <div class="font-normal w-6rem">{{ getClassAir(infoData?.infoClasses) }}</div>
          </div>
        </div>
        <div class="adults font-bold mb-2">
          <div class="flex">
            <div class="min-w-label">{{ $t('common.adults') }}:</div>
            <div class="font-normal w-6rem">{{ getFlightEconomy(infoData?.infoClasses)?.adults ?? 0 }}</div>
          </div>
        </div>
        <div class="child font-bold mb-2">
          <div class="flex">
            <div class="min-w-label">{{ $t('common.children') }}:</div>
            <div class="font-normal w-6rem">{{ getFlightEconomy(infoData?.infoClasses)?.children ?? 0 }}</div>
          </div>
        </div>
        <div class="infant font-bold mb-2">
          <div class="flex">
            <div class="min-w-label">{{ $t('common.infant') }}:</div>
            <div class="font-normal w-6rem">{{ getFlightEconomy(infoData?.infoClasses)?.infants ?? 0 }}</div>
          </div>
        </div>
        <div class="total font-bold">
          <div class="flex">
            <div class="min-w-label">{{ $t('worksheet.total_passengers_economy') }}:</div>
            <div class="font-normal w-6rem">{{ getFlightEconomy(infoData?.infoClasses)?.totalSeats ?? 0 }}</div>
          </div>
        </div>
      </div>
      <div class="flex flex-column align-items-end">
        <div class="hotel-room-cat font-bold mb-2 mt-2">
          <div class="flex">
            <div class="min-w-label">{{ $t('common.class') }}:</div>
            <div class="font-normal w-6rem">{{ getClassAirBusiness(infoData?.price) }}</div>
          </div>
        </div>
        <div class="adults font-bold mb-2">
          <div class="flex">
            <div class="min-w-label">{{ $t('common.adults') }}:</div>
            <div class="font-normal w-6rem">{{ getFlightBusiness(infoData?.infoClasses)?.adults ?? 0 }}</div>
          </div>
        </div>
        <div class="child font-bold mb-2">
          <div class="flex">
            <div class="min-w-label">{{ $t('common.children') }}:</div>
            <div class="font-normal w-6rem">{{ getFlightBusiness(infoData?.infoClasses)?.children ?? 0 }}</div>
          </div>
        </div>
        <div class="infant font-bold mb-2">
          <div class="flex">
            <div class="min-w-label">{{ $t('common.infant') }}:</div>
            <div class="font-normal w-6rem">{{ getFlightBusiness(infoData?.infoClasses)?.infants ?? 0 }}</div>
          </div>
        </div>
        <div class="total font-bold">
          <div class="flex">
            <div class="min-w-label">{{ $t('worksheet.total_passengers_business') }}:</div>
            <div class="font-normal w-6rem">{{ getFlightBusiness(infoData?.infoClasses)?.totalSeats ?? 0 }}</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
